import '@fontsource/manrope/400.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/manrope/800.css';
import '@fontsource/yatra-one/400.css';
import '@fontsource/noto-sans-devanagari/400.css';
import '@fontsource/noto-sans-devanagari/700.css';
import '@fontsource/noto-sans-gujarati/400.css';
import '@fontsource/noto-sans-gujarati/700.css';
import './styles/index.css';

import { createSettingsStore } from '@mendikot/app-core';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './app/App';
import { AppProvider, useUi } from './app/context';
import { ThemeProvider } from './app/theme';
import { isStandalone } from './lib/device';
import { browserStore, createWebServices } from './platform/services';
import { captureInstallPrompt } from './screens/prompts';

const kv = browserStore();
const settings = createSettingsStore(kv, navigator.languages);
const services = createWebServices(kv, () => settings.getState().lang);

// Consent decides whether analytics, performance and crash reporting run at all (plan §5.1).
const consent = settings.getState().analyticsConsent;
if (consent !== 'unknown') services.applyConsent(consent === 'granted');

void services.remote.start();
// DPDP: a new visitor gets no account (and no Auth/App Check load) until WelcomeGate confirms 18+.
if (settings.getState().ageConfirmedAt !== null) {
  void services.auth
    .ensureSignedIn()
    .catch((error) => services.crash.captureError(error, { where: 'auth_boot' }));
}

const launchMode = isStandalone() ? 'standalone' : 'browser';
services.analytics.setUserProperties({
  app_platform: 'web',
  launch_mode: launchMode,
  lang: settings.getState().lang,
});
services.analytics.log('app_open', { launch_mode: launchMode });

// Attribute opens from push notifications (links carry ?src=push&nid=…&type=…).
const query = new URLSearchParams(window.location.search);
if (query.get('src') === 'push') {
  services.analytics.log('notification_open', {
    type: query.get('type') ?? 'unknown',
    nid: query.get('nid') ?? '',
  });
}

captureInstallPrompt(() => services.analytics.log('pwa_install', {}));

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  // A new version is applied only between hands (never mid-trick), then the page reloads into it.
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      const apply = () => void updateSW(true);
      if (!useUi.getState().inHand) return apply();
      const stop = useUi.subscribe((ui) => {
        if (ui.inHand) return;
        stop();
        apply();
      });
    },
    // Long sessions (a game night) still pick up new versions.
    onRegisteredSW(_url, registration) {
      if (registration) setInterval(() => void registration.update(), 60 * 60_000);
    },
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppProvider value={{ services, settings }}>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </AppProvider>
  </StrictMode>,
);
